import { buildAuditCursorPrompt } from "@bb-dw-core/src/lib/audit-cursor-prompt";
import { supabase } from "@/lib/supabase";

export { formatAuditMatchSummary } from "@/audit/format-audit-match-summary";

export type AuditRunStatus = "running" | "pass" | "fail" | "corrected" | "error";

export interface AuditDiffRow {
  key?: string;
  region: string;
  section: string;
  syncoreName: string;
  metric: string;
  expected?: number;
  actual?: number | null;
  delta?: number | null;
}

export interface AuditDiffs {
  mismatched: AuditDiffRow[];
  missingInWarehouse: AuditDiffRow[];
  extraInWarehouse: AuditDiffRow[];
}

export interface AuditRunRow {
  id: number;
  check_type: string;
  status: AuditRunStatus;
  system: string;
  activity_date: string;
  trigger_type: string;
  started_at: string;
  finished_at: string | null;
  expected_rows: number;
  matched: number;
  mismatched_count: number;
  missing_count: number;
  extra_count: number;
  diffs: AuditDiffs;
  self_correct_attempted: boolean;
  self_correct_sync_run_id: number | null;
  cursor_prompt: string | null;
  error_detail: string | null;
}

function emptyDiffs(): AuditDiffs {
  return { mismatched: [], missingInWarehouse: [], extraInWarehouse: [] };
}

function parseAuditDiffs(raw: unknown): AuditDiffs {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return emptyDiffs();
  const o = raw as Record<string, unknown>;
  const asRows = (v: unknown): AuditDiffRow[] => (Array.isArray(v) ? (v as AuditDiffRow[]) : []);
  return {
    mismatched: asRows(o.mismatched),
    missingInWarehouse: asRows(o.missingInWarehouse),
    extraInWarehouse: asRows(o.extraInWarehouse),
  };
}

type AuditRunDbRow = {
  id: number | null;
  check_type: string | null;
  status: string | null;
  system: string | null;
  activity_date: string | null;
  trigger_type: string | null;
  started_at: string | null;
  finished_at: string | null;
  expected_rows: number | null;
  matched: number | null;
  mismatched_count: number | null;
  missing_count: number | null;
  extra_count: number | null;
  diffs: unknown;
  self_correct_attempted: boolean | null;
  self_correct_sync_run_id: number | null;
  cursor_prompt: string | null;
  error_detail: string | null;
};

function mapAuditRunRow(row: AuditRunDbRow): AuditRunRow | null {
  if (
    row.id == null ||
    !row.check_type ||
    !row.status ||
    !row.system ||
    !row.activity_date ||
    !row.trigger_type ||
    !row.started_at
  ) {
    return null;
  }
  return {
    id: row.id,
    check_type: row.check_type,
    status: row.status as AuditRunStatus,
    system: row.system,
    activity_date: row.activity_date,
    trigger_type: row.trigger_type,
    started_at: row.started_at,
    finished_at: row.finished_at,
    expected_rows: row.expected_rows ?? 0,
    matched: row.matched ?? 0,
    mismatched_count: row.mismatched_count ?? 0,
    missing_count: row.missing_count ?? 0,
    extra_count: row.extra_count ?? 0,
    diffs: parseAuditDiffs(row.diffs),
    self_correct_attempted: row.self_correct_attempted ?? false,
    self_correct_sync_run_id: row.self_correct_sync_run_id,
    cursor_prompt: row.cursor_prompt,
    error_detail: row.error_detail,
  };
}

/** Keep the newest run per system + activity date (re-runs supersede older failures). */
export function latestAuditRunPerSlot(runs: AuditRunRow[]): AuditRunRow[] {
  const bySlot = new Map<string, AuditRunRow>();
  for (const run of runs) {
    const key = `${run.check_type}|${run.system}|${run.activity_date}`;
    if (!bySlot.has(key)) bySlot.set(key, run);
  }
  return [...bySlot.values()].sort((a, b) => b.started_at.localeCompare(a.started_at));
}

export async function fetchAuditRuns(limit = 100): Promise<AuditRunRow[]> {
  const { data, error } = await supabase
    .from("audit_runs")
    .select(
      "id, check_type, status, system, activity_date, trigger_type, started_at, finished_at, expected_rows, matched, mismatched_count, missing_count, extra_count, diffs, self_correct_attempted, self_correct_sync_run_id, cursor_prompt, error_detail",
    )
    .order("started_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  const mapped = (data ?? [])
    .map((row) => mapAuditRunRow(row as AuditRunDbRow))
    .filter((row): row is AuditRunRow => row != null);
  return latestAuditRunPerSlot(mapped);
}

export function auditStatusLabel(status: AuditRunStatus): string {
  switch (status) {
    case "pass":
      return "Pass";
    case "corrected":
      return "Corrected";
    case "fail":
      return "Fail";
    case "running":
      return "Running";
    case "error":
      return "Error";
  }
}

export type AuditStatusTone = "success" | "info" | "danger" | "warning";

export function auditStatusTone(status: AuditRunStatus): AuditStatusTone {
  switch (status) {
    case "pass":
      return "success";
    case "corrected":
      return "info";
    case "fail":
      return "danger";
    case "running":
      return "warning";
    case "error":
      return "danger";
  }
}

export function formatAuditTimestamp(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-CA", {
    timeZone: "America/Toronto",
    dateStyle: "medium",
    timeStyle: "short",
  });
}

export function buildFallbackCursorPrompt(run: AuditRunRow): string {
  return buildAuditCursorPrompt({
    auditRunId: run.id,
    system: run.system,
    activityDate: run.activity_date,
    checkType: run.check_type,
    triggerType: run.trigger_type,
    status: run.status === "error" ? "error" : "fail",
    startedAt: run.started_at,
    finishedAt: run.finished_at,
    expectedRows: run.expected_rows,
    matched: run.matched,
    mismatchedCount: run.mismatched_count,
    missingCount: run.missing_count,
    extraCount: run.extra_count,
    diffs: run.diffs ?? emptyDiffs(),
    selfCorrectAttempted: run.self_correct_attempted,
    selfCorrectSyncRunId: run.self_correct_sync_run_id,
    errorDetail: run.error_detail,
  });
}

export function resolveAuditCursorPrompt(run: AuditRunRow): string | null {
  if (run.status !== "fail" && run.status !== "error") return null;
  return buildFallbackCursorPrompt(run);
}
